import type { Ref } from 'vue'

import { useListsStore } from '@shared/model'
import { resolveUrl } from '@shared/lib/url'
import { AppRoute } from '@shared/config'

import { DisciplineGeneralModal } from './enums'
import { useDisciplineStore } from './store'
import { useDisciplineGeneralInfoStore } from './general-info/store'
import { useDisciplineDestroyApi } from '../api/destroy.hook'
import { getDisciplineName } from './discipline-general-info'

interface DisciplineModalDeleteReturn {
  refIsOpen: ComputedRef<boolean>
  refIsLoading: Ref<boolean>
  refDetail: ComputedRef<string>
  closeModal: () => void
  confirmDelete: () => Promise<void>
}

export const useDisciplineModalDelete = (): DisciplineModalDeleteReturn => {
  const disciplineStore = useDisciplineStore()
  const generalInfoStore = useDisciplineGeneralInfoStore()
  const listsStore = useListsStore()

  const refIsLoading = ref(false)

  const refIsOpen = computed<boolean>(() => generalInfoStore.getActiveModal === DisciplineGeneralModal.Delete)

  const refDetail = computed<string>(() => {
    const name = getDisciplineName(disciplineStore.getDetails?.code ?? '', listsStore.getLists?.discipline.code)
    return `Are you sure you want to delete ${name}? All of data will be permanently removed. This action cannot be undone.`
  })

  const closeModal = (): void => {
    generalInfoStore.setActiveModal(null)
  }

  /** Every URL of a deleted discipline answers 404, so a successful delete leaves for the kid page. */
  const confirmDelete = async (): Promise<void> => {
    const details = disciplineStore.getDetails
    if (!details || refIsLoading.value) return

    const isDeleted = await useDisciplineDestroyApi({
      disciplineId: details.id,
      onStart: () => { refIsLoading.value = true },
      onFinish: () => { refIsLoading.value = false },
    })

    closeModal()

    const kidUrl = isDeleted ? resolveUrl(AppRoute.KidsShow, { id: details.kid_id }) : null
    if (kidUrl) await navigateTo(kidUrl)
  }

  return {
    refIsOpen,
    refIsLoading,
    refDetail,
    closeModal,
    confirmDelete,
  }
}
